import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { getBillingExplorerWidgetTooltipValueFormatter } from "./utils/helpers/getBillingExplorerWidgetTooltipValueFormatter";
import { getHighChartsYAxisLabelsFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsYAxisLabelsFormatter";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { TooltipMarkupGenerator } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { stackedChartHeadingLabelFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/stackedChartHeadingLabelFormatter";
import { getPieChartTooltipValueFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getPieChartTooltipValueFormatter";
import {
  DateDataPointNullable,
  Dates,
  ForecastedCostRe,
  ResourceExplorerChartData,
  WidgetCostBreakdownVisualization,
} from "@/services/cloudchipr.api";
import { useBillingExplorerWidgetChartDataHook } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/billing-explorer/components/high-chart-view/utils/hooks/useBillingExplorerWidgetChartData.hook";
import { chartTypeFromRechartToHighCharts } from "@/components/utils/helpers/chartTypeFromRechartToHighCharts";
import { HighchartsChartType } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";

interface BillingExplorerWidgetHighChartProps {
  dates?: Dates;
  loading?: boolean;
  hasAggregations?: boolean;
  quarterStartMonth?: number;
  forecastedCosts?: ForecastedCostRe;
  data?: ResourceExplorerChartData[];
  frequency: DateDataPointNullable | undefined;
  visualization: WidgetCostBreakdownVisualization;
}

export const BillingExplorerWidgetHighChart: FC<
  BillingExplorerWidgetHighChartProps
> = ({
  data,
  dates,
  loading,
  frequency,
  visualization,
  forecastedCosts,
  hasAggregations,
  quarterStartMonth,
}) => {
  const {
    scale,
    chart_type,
    show_legends,
    cumulative_line,
    show_data_labels,
    display_forecast,
    chart_base_type = "cost",
  } = visualization;

  const chartType = chartTypeFromRechartToHighCharts(
    chart_type ?? "stacked_column",
  ) as HighchartsChartType;

  const {
    chartData,
    chartDataCostBasedMap,
    chartDataPercentBasedMap,
    totalCostsMap,
  } = useBillingExplorerWidgetChartDataHook(
    data,
    chart_base_type,
    frequency,
    quarterStartMonth,
    display_forecast,
    forecastedCosts,
    dates,
  );

  const columnDataLabels = useMemo(
    () => getHighChartsColumnDataLabels(chartType, chart_base_type),
    [chart_base_type, chartType],
  );

  const yAxisLabelsFormatter = useMemo(
    () => getHighChartsYAxisLabelsFormatter(chart_base_type),
    [chart_base_type],
  );

  const tooltipValueFormatter = useMemo(
    () =>
      getBillingExplorerWidgetTooltipValueFormatter(
        chartDataCostBasedMap,
        chartDataPercentBasedMap,
        totalCostsMap,
        chartData,
      ),
    [chartDataCostBasedMap, chartDataPercentBasedMap, totalCostsMap, chartData],
  );

  const pieChartTooltipValueFormatter: TooltipMarkupGenerator = useMemo(
    () => getPieChartTooltipValueFormatter(dates, quarterStartMonth),
    [dates, quarterStartMonth],
  );

  return (
    <HighchartsProvider>
      <Box height={hasAggregations ? 360 : 440}>
        <MultiTypeHighcharts
          chartScale={scale}
          chartType={chartType}
          chartData={chartData}
          showLegends={show_legends}
          showDataLabels={show_data_labels}
          loading={loading ? loadingProps : false}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          percentBased={chart_base_type === "percent"}
          cumulativeLine={cumulative_line && chart_base_type === "cost"}
          noDataMessage="No data by your selected date range and filters."
          columnDataLabels={columnDataLabels}
          tooltipMarkupGenerator={
            chartType === "pie"
              ? pieChartTooltipValueFormatter
              : tooltipValueFormatter
          }
          stackedChartHeadingLabelFormatter={
            chart_base_type === "cost"
              ? stackedChartHeadingLabelFormatter
              : undefined
          }
        />
      </Box>
    </HighchartsProvider>
  );
};

const loadingProps = { showLoader: false };
